import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ChipProps,
  Grid,
  Typography,
  FormControl,
  TypographyProps,
  Stack,
  Chip,
  Button,
  TextField,
  TextFieldProps,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

import { useAppSelector } from '../../../hooks';
import { adminGetQuizById, getQuizState } from '../../../store/QuizSlice';
import {
  adminGetQuestionList,
  getQuestionState,
  adminLoadMoreQuestionList,
  IQuestion,
  IQuestionSubTopic,
  adminGetQuestionById,
  resetQuestionList,
  resetQuestion,
} from '../../../store/QuestionSlice';
import { getQuestionDifficultyChipColor } from '../../../helper';
import ImageContainer from '../ManageQuestions/components/ImageContainer';
import QuizDetailHeader from './components/QuizDetailHeader';
import CustomGrayContainer from '../../../components/Containers/CustomGrayContainer';
import QuestionCard from '../ManageQuestions/components/QuestionCard';
import DetailViewCard from '../../../components/Card/DetailViewCard';
import QuestionDetailsManager from '../ManageQuestions/components/ManageDetails/QuestionDetailsManager';
import MarkingSchemeManager from '../ManageQuestions/components/ManageDetails/MarkingSchemeManager';
import ReadOnlyAnswerOption from '../ManageQuestions/components/AnswerManagerV2/ReadOnlyAnswerOption';

const inputStyles: TextFieldProps['sx'] = {
  backgroundColor: 'background.paper',
  borderRadius: '4px',
  mb: '10px',
};

const label: TypographyProps = {
  variant: 'subtitle2',
  fontWeight: 600,
  sx: { mb: '2px' },
};

const readOnlyTextFieldProps: TextFieldProps = {
  fullWidth: true,
  size: 'small',
  sx: inputStyles,
  InputProps: { readOnly: true },
};

const ViewQuiz: React.FC = () => {
  const UrlParams = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Store
  const quizState = useAppSelector(getQuizState);
  const questionState = useAppSelector(getQuestionState);

  const questyStringParams = new URLSearchParams(location.search);
  const urlQuestionId = questyStringParams.get('questionId') || '';

  // State
  const [filterData, setFilterData] = useState({ limit: 30, offset: 0 });
  const [selectedQuestion, setSelectedQuestion] = useState<
    IQuestion | undefined
  >(undefined);

  const quizData = quizState.quiz;
  const quizTopicData = quizData && quizData.topic ? quizData.topic : undefined;
  const subTopics: Array<IQuestionSubTopic> = [];
  const questionList = [...questionState.list];
  const quizSubTopicStringIds: string[] = [];
  const quizSubTopics = (quizData &&
  quizData.subTopics &&
  quizData.subTopics.length
    ? quizData.subTopics
    : []
  ).filter((item) => item.subTopic);

  (quizSubTopics || []).forEach((quizSubTopic) => {
    const quizSubTopicId = quizSubTopic.subTopic?._id || '';

    if (
      quizSubTopicId &&
      !quizSubTopicStringIds.includes(quizSubTopic.subTopic?._id || '')
    ) {
      quizSubTopicStringIds.push(quizSubTopicId);
    }
  });

  let subject = '';
  let curriculum = '';
  let grade = '';
  let subjectCode = '';

  if (selectedQuestion && selectedQuestion.subTopics) {
    selectedQuestion.subTopics.forEach((subTopic) => {
      subTopics.push(subTopic);
    });
  }

  if (quizTopicData) {
    subject = quizTopicData.subject;
    curriculum = quizTopicData.curriculum;
    grade = quizTopicData.grade;
    subjectCode = quizTopicData.subjectCode;
  }

  if (
    questionState.question &&
    !questionList.some(
      (questionListItem) => questionListItem._id === questionState.question?._id
    )
  ) {
    questionList.push(questionState.question);
  }

  useEffect(() => {
    dispatch(resetQuestionList());
    dispatch(resetQuestion());
  }, []);

  useEffect(() => {
    if (UrlParams.quizId) {
      dispatch(adminGetQuizById(UrlParams.quizId));
    }
  }, [UrlParams.quizId]);

  useEffect(() => {
    if (quizData && quizData.subTopics) {
      dispatch(
        adminGetQuestionList({
          ...filterData,
          subTopicIds: quizSubTopicStringIds,
        })
      );
    }
  }, [quizData]);

  useEffect(() => {
    if (urlQuestionId) {
      dispatch(adminGetQuestionById(urlQuestionId));
    }
  }, [urlQuestionId]);

  useEffect(() => {
    if (questionState.question) {
      setSelectedQuestion(questionState.question);
    }
  }, [questionState.question]);

  useEffect(() => {
    if (questionState.list.length && !urlQuestionId) {
      onQuestionChange(questionState.list[0]);
    }
  }, [questionState.list]);

  const handleLoadMore = () => {
    if (quizData && quizData.subTopics) {
      dispatch(
        adminLoadMoreQuestionList({
          ...filterData,
          offset: questionState.list.length,
          subTopicIds: quizSubTopicStringIds,
        })
      );
      setFilterData((prevFilterData) => ({
        ...prevFilterData,
        offset: questionState.list.length,
      }));
    }
  };

  const onQuestionChange = (question: IQuestion) => {
    if (!urlQuestionId) {
      navigate({
        search: `?questionId=${question._id}`,
      });
    } else if (urlQuestionId !== question._id) {
      questyStringParams.set('questionId', question._id);

      navigate({
        search: `?${questyStringParams.toString()}`,
      });
    }
  };

  return (
    <div>
      {quizData && (
        <div>
          <ImageContainer
            isAssessment={quizData?.assessment || false}
            curriculum={curriculum}
            subject={subject}
            grade={grade}
            subjectCode={subjectCode}
            style={{ margin: '0' }}
            subTopics={quizSubTopics.map((quizSubTopic) => {
              const subTopicText = quizSubTopic.subTopic?.name || '';
              const paperText = quizSubTopic.paper || '';

              return `${subTopicText} - ${paperText}`;
            })}
          />
          <QuizDetailHeader quiz={quizData} />
          <div style={{ padding: '30px' }}>
            <Grid container spacing={2} sx={{ pb: '15px' }}>
              <Grid item xs={12} sm={12} md={4} lg={3}>
                <div
                  style={{ margin: '15px 0' }}
                  className="question-list-item-container"
                >
                  {questionList.length
                    ? questionList.map((question, index) => {
                        const _selected =
                          question._id === selectedQuestion?._id;
                        const _title = `Question ${(index + 1)
                          .toString()
                          .padStart(2, '0')}`;
                        const _description = '';
                        const _chips: Array<ChipProps> = [
                          {
                            color: getQuestionDifficultyChipColor(
                              question.difficulty
                            ),
                            label: question.difficulty.toLowerCase(),
                          },
                        ];
                        if (question.isApproved) {
                          _chips.push({
                            color: 'info',
                            label: 'Approved',
                          });
                        }
                        return (
                          <QuestionCard
                            key={index}
                            isDisabled={
                              questionState.status === 'loading' ||
                              quizState.status === 'loading'
                            }
                            isSelected={_selected}
                            title={_title}
                            description={_description}
                            chips={_chips}
                            onClick={() => onQuestionChange(question)}
                          />
                        );
                      })
                    : null}
                  {questionState.count > questionState.list.length && (
                    <Stack direction="row" justifyContent="center">
                      <Button
                        disabled={questionState.status === 'loading'}
                        onClick={handleLoadMore}
                      >
                        Load More
                      </Button>
                    </Stack>
                  )}
                </div>
              </Grid>
              {selectedQuestion && (
                <Grid item xs={12} sm={12} md={8} lg={6}>
                  <CustomGrayContainer>
                    {selectedQuestion.details && (
                      <QuestionDetailsManager
                        mode="readOnly"
                        details={selectedQuestion.details}
                      />
                    )}
                    {selectedQuestion.answers && (
                      <div style={{ marginBottom: '15px' }}>
                        <Typography fontWeight={600} sx={{ mb: '5px' }}>
                          Answer Options
                        </Typography>
                        <div>
                          {selectedQuestion.answers.length > 0 &&
                            selectedQuestion.answers.map((answer, index) => {
                              return (
                                <ReadOnlyAnswerOption
                                  key={index}
                                  itemIndex={index}
                                  answer={answer}
                                />
                              );
                            })}
                        </div>
                      </div>
                    )}
                    {selectedQuestion.markingSchema && (
                      <MarkingSchemeManager
                        mode="readOnly"
                        details={selectedQuestion.markingSchema}
                      />
                    )}
                  </CustomGrayContainer>
                </Grid>
              )}
              {selectedQuestion && (
                <Grid item xs={12} sm={12} md={12} lg={3}>
                  <CustomGrayContainer>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4} md={4} lg={12}>
                        <FormControl fullWidth>
                          <Typography {...label}>
                            Question Difficulty Level
                          </Typography>
                          <TextField
                            defaultValue={selectedQuestion.difficulty}
                            {...readOnlyTextFieldProps}
                          />
                        </FormControl>
                      </Grid>
                      {selectedQuestion.source && (
                        <Grid item xs={12} sm={4} md={4} lg={12}>
                          <FormControl fullWidth>
                            <Typography {...label}>Question Soruce</Typography>
                            <TextField
                              defaultValue={selectedQuestion.source}
                              {...readOnlyTextFieldProps}
                            />
                          </FormControl>
                        </Grid>
                      )}
                      {selectedQuestion.marks && (
                        <Grid item xs={12} sm={4} md={4} lg={12}>
                          <FormControl fullWidth>
                            <Typography {...label}>Marks</Typography>
                            <TextField
                              defaultValue={selectedQuestion.marks.toString()}
                              {...readOnlyTextFieldProps}
                            />
                          </FormControl>
                        </Grid>
                      )}
                      {selectedQuestion.tags.length > 0 && (
                        <Grid item xs={12}>
                          <Typography {...label}>Tags</Typography>
                          <Stack direction="row" flexWrap="wrap">
                            {selectedQuestion.tags.map((tag, index) => (
                              <Chip
                                key={index}
                                size="small"
                                label={tag}
                                sx={{ m: '5px 5px 0 0' }}
                              />
                            ))}
                          </Stack>
                        </Grid>
                      )}
                      <Grid item xs={12} style={{ marginBottom: '-24px' }}>
                        <Typography {...label}>Papers</Typography>
                      </Grid>
                      {quizTopicData &&
                      quizTopicData.papers &&
                      quizTopicData.papers.length
                        ? quizTopicData.papers.map((paperItem, index) => {
                            // const isChecked = subTopics.some(
                            //   (questionSubTopic) =>
                            //     questionSubTopic?.subTopic?._id.toString() ===
                            //       quizSubTopicData?._id &&
                            //     questionSubTopic.paper === paperItem.name
                            // );

                            const isChecked = false;

                            return (
                              <Grid
                                item
                                xs={12}
                                sm={4}
                                md={3}
                                lg={6}
                                key={index}
                              >
                                <FormControlLabel
                                  label={paperItem.name}
                                  control={
                                    <Checkbox readOnly checked={isChecked} />
                                  }
                                />
                              </Grid>
                            );
                          })
                        : null}
                      {subTopics.length > 0 && (
                        <Grid item xs={12}>
                          <Typography {...label}>SubTopics</Typography>
                          <div>
                            {subTopics.map((questionSubTopic, index) => (
                              <DetailViewCard
                                key={index}
                                title="Sub Topic"
                                titleProps={{ variant: 'caption' }}
                                descriptionProps={{
                                  className: 'ellipsis-text',
                                }}
                                containerProps={{
                                  sx: { backgroundColor: 'background.default' },
                                }}
                                description={`${
                                  questionSubTopic.subTopic &&
                                  questionSubTopic.subTopic.name
                                    ? questionSubTopic.subTopic.name
                                    : '-'
                                } - ${questionSubTopic.paper || ''}`}
                              />
                            ))}
                          </div>
                        </Grid>
                      )}
                    </Grid>
                  </CustomGrayContainer>
                </Grid>
              )}
            </Grid>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewQuiz;
